import { reportBelongsToSite } from './reportSiteIdentity';
/**
 * Preflight check.
 *
 * Pure helper that walks a report type's requiredSedSections against a loaded
 * SED and returns per-section availability + freshness. Screening reports can
 * proceed with explicit source gaps; mature report prerequisites remain strict.
 *
 * Status taxonomy (New_Report_Wizard Cleanup Prompt 3 — replaces the
 * old `green | amber | red` + `MISSING` badge, which produced
 * self-contradictory copy like "0 days old · MISSING"):
 *
 *   - 'fresh'              — data exists, age ≤ TTL.
 *   - 'stale'              — data exists, age > TTL.
 *   - 'anomalous'          — data returned but failed a cross-validation
 *                            check (SED status === 'anomalous').
 *   - 'queried-empty'      — provider returned no data on the most recent
 *                            query (SED status === 'unavailable').
 *   - 'never-queried'      — no query attempted yet (section absent from
 *                            the SED, or no SED at all).
 *   - 'provider-error'     — the most recent query failed (reserved — the
 *                            SED schema doesn't carry an HTTP status yet,
 *                            so this isn't currently emitted, but the
 *                            UI handles it).
 *   - 'pending-collection' — bootstrap mode only: the section is missing
 *                            but the upcoming run will collect it.
 *
 * `'queried-empty'`, `'never-queried'`, and `'provider-error'` are blocking gaps
 * unless the report's screening policy admits partial evidence; `'stale'` and
 * `'anomalous'` are advisory (the old `amber`); `'fresh'` and
 * `'pending-collection'` don't block and aren't warnings.
 *
 * Bootstrap mode (set when the run will collect missing sections itself,
 * i.e. first-ever report for a site or a site whose SED isn't complete):
 *   - Missing/unavailable sections become 'pending-collection'
 *     ("Will be collected during this run, ~30s") instead of a blocking
 *     status — except when the section's primary provider is not in
 *     `wiredProviders`. Those stay 'never-queried' (blocking) because the
 *     run can't fix them; admin needs to wire the provider.
 */
import type { SiteEvidenceDocument, EvidenceSection } from '../types/siteEvidence';
import type { SiteEntity } from '../types/site';
import type { SiteRagReport } from '../types/siteRag';
import { SED_SECTION_TTL_DAYS, SED_SECTION_LABELS } from './sedStaleness';
import type { SedSectionId } from '../types/siteEvidence';
import { allowsScreeningWithSourceGaps } from './reportGeneration/screeningPolicy';
import { assessGenerationImpactTestFitApplicability } from './generationImpact/testFitApplicability';
import { unavailableSentence } from './evidenceUnavailableReason';
import {
  explicitUnknownIntentFieldLabel,
  explicitUnknownIntentRecommendation,
  isIntentFieldExplicitlyUnknown,
} from './intent/explicitUnknown';
import { recordedUnknownFieldOfSection } from './stampBtmGroundingFromIntent';

/**
 * Maps SED section keys to the primary provider id that fills them. Used in
 * bootstrap mode to decide whether a missing section can be collected by
 * the upcoming run (provider wired) or is a real blocker (provider unwired).
 *
 * Keep aligned with SECTION_TO_PROVIDER_ID in src/services/sedBootstrap.ts.
 * Sections not in this map have no single primary provider (e.g. regulatory
 * is derived from parcel + AI synthesis) and are always treated as
 * "collectable in bootstrap mode".
 */
export const SECTION_PRIMARY_PROVIDER: Record<string, string> = {
  climate:        'open-meteo',
  waterResource:  'usgs-waters', // RD-1 (D)
  seismic:        'usgs-seismic',
  flood:          'fema-flood',
  grid:           'hifld-grid',
  environmental:  'usfws-env',
  waterStress:    'wri-aqueduct',
  electricity:    'eia-electricity',
  airQuality:     'airnow-aqi',
  solar:          'nrel-solar',
  wind:           'nrel-wind',
  nuclearInfra:   'nrc-reactors',
  connectivity:   'peeringdb-ix',
  siteResearch:   'site-research',
  parcel:         'regrid-parcels',
  fuelSupply:     'eia-natural-gas',
  technology:     'project-intent',
  cooling:        'project-intent',
  power:          'project-intent',
  firmCapacity:   'project-intent',
  lcoe:           'project-intent',
};

export type PreflightStatus =
  | 'fresh'
  | 'stale'
  | 'anomalous'
  | 'queried-empty'
  | 'never-queried'
  | 'provider-error'
  | 'pending-collection';

/** Statuses that BLOCK the Generate button (the old `red`). */
export const PREFLIGHT_BLOCKING_STATUSES: ReadonlySet<PreflightStatus> = new Set<PreflightStatus>([
  'queried-empty',
  'never-queried',
  'provider-error',
]);

/** Statuses that are advisory warnings but don't block (the old `amber`). */
export const PREFLIGHT_WARNING_STATUSES: ReadonlySet<PreflightStatus> = new Set<PreflightStatus>([
  'stale',
  'anomalous',
]);

export function isBlockingPreflightStatus(s: PreflightStatus): boolean {
  return PREFLIGHT_BLOCKING_STATUSES.has(s);
}
export function isWarningPreflightStatus(s: PreflightStatus): boolean {
  return PREFLIGHT_WARNING_STATUSES.has(s);
}

export interface PreflightSection {
  /** SED section ID, e.g. 'climate', 'connectivity'. */
  id: string;
  status: PreflightStatus;
  /** Age of the section's data in days; null when section is missing. */
  ageInDays: number | null;
  /** Days after which the section is considered stale; null when unknown. */
  ttlDays: number | null;
  /** HTTP status for 'provider-error' rows; null otherwise. */
  httpStatus: number | null;
  /** Human-readable explanation surfaced in the UI. */
  message: string;
  /**
   * Q3 — set only on an intent-derived section that is empty because the user
   * RECORDED its source Project Intent field as unknown (the field path). The
   * row's status — and therefore blocking / counting — is exactly what it would
   * be without it; only the wording differs: the message states the recorded
   * unknown and its follow-up instead of asking the user to complete the field.
   */
  recordedUnknownField?: string;
}

export interface PreflightResult {
  sections: PreflightSection[];
  /** Whether source coverage permits launch; screening retains gaps without blocking. */
  canGenerate: boolean;
  /** True when at least one section is a warning (stale/anomalous). */
  hasStaleData: boolean;
  /** Number of source gaps (screening may proceed with these disclosed). The name is
   *  retained from the legacy `red` taxonomy for call-site compatibility. */
  redCount: number;
  /** Number of warning sections (stale/anomalous). Retained name. */
  amberCount: number;
  /** Number of sections that will be collected by the upcoming bootstrap run. */
  pendingCollectionCount: number;
  /**
   * Caller-facing hint about why the result is what it is. Today the
   * only value is `'first-run'`, returned when sed is null AND
   * bootstrapMode is true — tells the wizard to render the welcome
   * card instead of an enumerated "missing section" list (which
   * would instruct the user to do exactly what they're about to do).
   * Undefined for normal results.
   */
  reason?: 'first-run';
}

// Design selections belong to the relevant advanced report. Their absence
// is valid during options screening; this does not stamp them as known in SED.
const DEFERRED_SCREENING_SECTIONS: ReadonlySet<string> = new Set([
  'technology', 'cooling', 'firmCapacity', 'lcoe',
]);

function intentSectionSetupMessage(id: string): string {
  switch (id) {
    case 'technology': return 'A generation technology has not been selected. Screening compares the available options; configure a selected design in the relevant advanced report when needed.';
    case 'cooling': return 'Cooling design is undecided. Screening compares cooling options; enter a selected design and its assumptions in the relevant advanced report when needed.';
    case 'firmCapacity': return 'Firm-capacity requirements and a supported supply case are unresolved. Configure these in Generation & Impact when assessing a selected design.';
    case 'lcoe': return 'Project cost assumptions are unresolved. Configure the financial model when preparing a financial assessment.';
    case 'power': return 'Load scale is unresolved. Record a known capacity range or phases in Intent, or keep the scope explicitly unknown for screening.';
    default: return 'This project-derived evidence is unresolved. Review the inputs for the relevant report before assessing a selected design.';
  }
}

const DEFAULT_FRESH_DAYS = 30;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

export interface RunPreflightOptions {
  /** Shared launch policy; source gaps stay visible on screening refinements. */
  reportTypeId?: import('../config/reportTypes').ReportTypeId;
  now?: Date;
  /** Override the staleness boundary for EVERY section. When omitted,
   *  each section uses its per-section TTL from SED_SECTION_TTL_DAYS
   *  (falling back to 30 days for ids not in that table). */
  freshDays?: number;
  /** True when the run will collect missing sections itself (first-ever report
   *  for a site, or a site whose SED isn't yet `complete`). Reclassifies
   *  missing/unavailable sections to 'pending-collection' so the user isn't
   *  blocked on the data the run is about to fetch. */
  bootstrapMode?: boolean;
  /** Provider ids the deployment can actually call. When a missing section's
   *  primary provider isn't in this set, bootstrap mode keeps it blocking
   *  because the run can't fix it — admin needs to wire the provider. Omit
   *  to treat every provider as wired. */
  wiredProviders?: Set<string>;
}

/**
 * Q3 — the Project Intent field each intent-derived section is stamped from.
 * Fallback for SEDs stamped before recorded-unknown sections carried their own
 * marker (`recordedUnknownFieldOfSection`): the SED's at-compile full intent is
 * consulted instead.
 */
export const INTENT_SECTION_SOURCE_FIELD: Readonly<Record<string, string>> = {
  technology: 'nuclearInterest',
  cooling: 'coolingStrategy',
  power: 'powerRequirement',
  firmCapacity: 'capacityCreditIntent',
};

/** The recorded-unknown field an empty intent-derived section stands for, or null. */
function recordedUnknownFieldForIntentSection(
  sed: SiteEvidenceDocument,
  id: string,
  section: EvidenceSection<unknown>,
): string | null {
  const stamped = recordedUnknownFieldOfSection(section);
  if (stamped) return stamped;
  const field = INTENT_SECTION_SOURCE_FIELD[id];
  if (!field) return null;
  const intent = sed.fullProjectIntent;
  // A declared "no nuclear" is a concrete answer, not an unknown.
  if (id === 'technology' && intent?.nuclearInterest === 'no') return null;
  return isIntentFieldExplicitlyUnknown(intent, field) ? field : null;
}

/** Q3 — preflight copy for a section left empty by a user-recorded unknown. */
export function recordedUnknownPreflightMessage(sectionId: string, field: string): string {
  const label = SED_SECTION_LABELS[sectionId as SedSectionId] ?? sectionId;
  return `${label} has no data because ${explicitUnknownIntentFieldLabel(field)} was recorded as UNKNOWN by the user — `
    + `that is the user's answer, not a missing input. This source gap stays open until the follow-up is resolved. `
    + `Open follow-up: ${explicitUnknownIntentRecommendation(field)}`;
}

/** Per-section staleness boundary in days. */
function ttlFor(id: string, freshDaysOverride: number | undefined): number {
  if (freshDaysOverride != null) return freshDaysOverride;
  return SED_SECTION_TTL_DAYS[id as SedSectionId] ?? DEFAULT_FRESH_DAYS;
}

/**
 * Run preflight against a SED for the listed required sections.
 *
 * `sed` may be `null` when the site has no SED yet — in that case every
 * required section is reported as `never-queried` (or `pending-collection`
 * under bootstrapMode).
 *
 * `opts.now` and `opts.freshDays` are injectable for testability.
 */
export function runPreflight(
  sed: SiteEvidenceDocument | null,
  requiredSectionIds: string[],
  opts: RunPreflightOptions = {},
): PreflightResult {
  const now = opts.now ?? new Date();
  const freshDays = opts.freshDays;
  const bootstrapMode = opts.bootstrapMode === true;
  const wiredProviders = opts.wiredProviders;

  // First-run: when there's no SED at all AND we're in bootstrap mode,
  // returning a per-section list incorrectly tells the user that every
  // section is "missing" — which is what they're literally about to
  // fix by clicking Generate. Return an empty section list with a
  // `reason` flag so the wizard can render the welcome card instead
  // of an enumerated checklist.
  if (!sed && bootstrapMode) {
    return {
      sections: [],
      canGenerate: true,
      hasStaleData: false,
      redCount: 0,
      amberCount: 0,
      pendingCollectionCount: 0,
      reason: 'first-run',
    };
  }

  /**
   * Decide whether bootstrap mode is allowed to flip a blocking section to
   * 'pending-collection' for this id. True when (a) the section has no known
   * primary provider (it's derived, e.g. regulatory), or (b) the deployment
   * hasn't supplied a `wiredProviders` set, or (c) the provider is in the
   * wired set.
   */
  const bootstrapCanCollect = (id: string): boolean => {
    const provider = SECTION_PRIMARY_PROVIDER[id];
    if (!provider) return true;
    if (provider === 'project-intent') return true;
    if (!wiredProviders) return true;
    return wiredProviders.has(provider);
  };

  const row = (
    id: string,
    status: PreflightStatus,
    ageInDays: number | null,
    message: string,
    httpStatus: number | null = null,
  ): PreflightSection => ({ id, status, ageInDays, ttlDays: ttlFor(id, freshDays), httpStatus, message });

  // `technology` is reactor-design evidence, not the equipment BOM. An
  // explicitly non-nuclear Supply Chain assessment has no reactor to select.
  // Keep unresolved/contradictory nuclear scope and supplied evidence visible;
  // normal BOM authority and delivery-quality checks remain separate gates.
  const nonNuclearSupplyChain = opts.reportTypeId === 'supply-chain-risk'
    && sed?.fullProjectIntent?.nuclearInterest === 'no'
    && !assessGenerationImpactTestFitApplicability({
      projectIntent: sed.fullProjectIntent,
    }).hasNuclearPhase;
  const applicableSections = requiredSectionIds.filter(id => {
    const section = sed && (sed as unknown as Record<string, EvidenceSection<unknown> | undefined>)[id];
    if (id === 'technology' && nonNuclearSupplyChain
      && (section == null || section.status === 'unavailable')) return false;
    if (!allowsScreeningWithSourceGaps(opts.reportTypeId)
      || !DEFERRED_SCREENING_SECTIONS.has(id)) return true;
    // A supplied design can still carry stale/anomalous evidence. Keep its
    // advisory diagnostics visible; defer only missing/unavailable inputs.
    return section != null && section.status !== 'unavailable';
  });
  const sections: PreflightSection[] = applicableSections.map(id => {
    if (!sed) {
      if (bootstrapMode && bootstrapCanCollect(id)) {
        return row(id, 'pending-collection', null, 'Will be collected during this run (~30s).');
      }
      if (bootstrapMode) {
        // Missing AND provider not wired — admin needs to fix.
        return row(id, 'never-queried', null,
          `Provider for "${id}" is not wired in this deployment — admin must enable it before the run can collect this section.`);
      }
      return row(id, 'never-queried', null,
        'No site evidence document exists yet. This source can be collected automatically when a compatible analysis runs, or added in Site Evidence now.');
    }
    const section = (sed as unknown as Record<string, EvidenceSection<unknown> | undefined>)[id];
    if (!section) {
      if (bootstrapMode && bootstrapCanCollect(id)) {
        return row(id, 'pending-collection', null, 'Will be collected during this run (~30s).');
      }
      if (bootstrapMode) {
        return row(id, 'never-queried', null,
          `Provider for "${id}" is not wired in this deployment — admin must enable it before the run can collect this section.`);
      }
      if (SECTION_PRIMARY_PROVIDER[id] === 'project-intent') {
        return row(id, 'never-queried', null,
          intentSectionSetupMessage(id));
      }
      // An absent section does NOT imply an OLD document — a current SED is
      // missing a section whenever its provider has not run OR ran and failed
      // (e.g. the public-sentiment orchestrator's dispatch catch, which only
      // warns to the server console). The previous copy asserted "legacy
      // evidence document" and prescribed a refresh, which on a freshly-rebuilt
      // SED is both untrue and a loop: the refresh re-enters the same failing
      // provider. Say what is actually known, and name the escalation when a
      // retry does not help.
      // Display name, not the camelCase SED key — this string is read by users
      // on Path to GO and the Evidence ledger, where "publicSentiment" read as
      // an internal identifier presented as an instruction.
      const label = SED_SECTION_LABELS[id as SedSectionId] ?? id;
      return row(id, 'never-queried', null,
        `${label} has not been collected for this site — its provider has either not run yet or failed during the last rebuild. Refresh the data to retry it; if it stays empty after a refresh, the provider is failing and needs attention rather than another rebuild.`);
    }
    const collected = section.retrievedAt ? new Date(section.retrievedAt).getTime() : NaN;
    const ageInDays = Number.isFinite(collected)
      ? Math.floor((now.getTime() - collected) / MS_PER_DAY)
      : null;

    if (section.status === 'unavailable') {
      if (bootstrapMode && bootstrapCanCollect(id)) {
        return row(id, 'pending-collection', ageInDays, 'Will be collected during this run (~30s).');
      }
      if (SECTION_PRIMARY_PROVIDER[id] === 'project-intent') {
        // Q3 — same status (and so the same blocking and counting); a recorded
        // unknown only changes the sentence from an ask to the recorded unknown
        // plus its follow-up.
        const recordedUnknownField = recordedUnknownFieldForIntentSection(sed, id, section);
        if (recordedUnknownField) {
          return {
            ...row(id, 'queried-empty', ageInDays, recordedUnknownPreflightMessage(id, recordedUnknownField)),
            recordedUnknownField,
          };
        }
        return row(id, 'queried-empty', ageInDays, intentSectionSetupMessage(id));
      }
      // `unavailable` is four different situations wearing one status; the old
      // single line asserted the provider ANSWERED and prescribed "upload a
      // document", which is right for exactly one of them. Status and blocking
      // are unchanged — only the sentence. See evidenceUnavailableReason.
      return row(id, 'queried-empty', ageInDays, unavailableSentence(section));
    }
    if (section.status === 'anomalous') {
      return row(id, 'anomalous', ageInDays, section.anomalies?.[0] ?? 'Data flagged with anomaly — review before generating.');
    }
    // status === 'verified'
    const ttl = ttlFor(id, freshDays);
    if (ageInDays !== null && ageInDays >= ttl) {
      return row(id, 'stale', ageInDays, `${ageInDays} days old (TTL ${ttl}d) — consider refreshing.`);
    }
    return row(id, 'fresh', ageInDays,
      ageInDays !== null
        ? `Fresh (${ageInDays === 0 ? 'today' : ageInDays === 1 ? '1 day ago' : `${ageInDays} days ago`}).`
        : 'Verified.');
  });

  const redCount = sections.filter(s => isBlockingPreflightStatus(s.status)).length;
  const amberCount = sections.filter(s => isWarningPreflightStatus(s.status)).length;
  const pendingCollectionCount = sections.filter(s => s.status === 'pending-collection').length;

  return {
    sections,
    canGenerate: redCount === 0 || allowsScreeningWithSourceGaps(opts.reportTypeId),
    hasStaleData: amberCount > 0,
    redCount,
    amberCount,
    pendingCollectionCount,
  };
}

/** First-screen welcome mode requires coordinates and no actual matching report.
 * Historic reportIds alone are not evidence that an artifact still exists.
 * Later location-screening refinements use reportTypeId's source-gap policy;
 * the first-run welcome mode is separate from mature domain authority.
 */
export function isFirstReportForSite(
  site: Pick<SiteEntity, 'id' | 'name' | 'reportIds' | 'sedStatus' | 'location'>,
  allReports: ReadonlyArray<Pick<SiteRagReport, 'id' | 'siteId' | 'siteName'>>,
): boolean {
  // Preserve all legacy joins, but require a matching canonical report object.
  if (allReports.some(report => reportBelongsToSite(site, report))) return false;

  return site.sedStatus !== 'failed' && hasScreeningCoordinates(site);
}

/** Coordinates suitable for location screening, independent of report history. */
export function hasScreeningCoordinates(site: Pick<SiteEntity, 'location'>): boolean {
  const lat = site.location?.lat;
  const lng = site.location?.lng;
  return typeof lat === 'number'
    && typeof lng === 'number'
    && Number.isFinite(lat)
    && Number.isFinite(lng)
    && Math.abs(lat) <= 90
    && Math.abs(lng) <= 180
    && !(Math.abs(lat) < 0.01 && Math.abs(lng) < 0.01);
}

/**
 * Per-section help text for the blocked-row info tooltip. Maps section
 * IDs to specific upload / provider / contact guidance.
 */
export function preflightRedHelp(sectionId: string): string {
  const help: Record<string, string> = {
    climate: 'Climate data comes from Open-Meteo. Re-run data collection or upload a NOAA climate file.',
    waterStress: 'Water stress data comes from WRI Aqueduct. Re-run data collection or upload a watershed study.',
    seismic: 'Seismic data comes from USGS. Re-run data collection or upload a site-specific PSHA.',
    grid: 'Grid data comes from HIFLD. Re-run data collection or upload a utility service letter.',
    flood: 'Flood data comes from FEMA NFHL. Re-run data collection or upload a flood elevation certificate.',
    environmental: 'Environmental data comes from USFWS / NWI. Re-run data collection or upload a Phase I ESA.',
    nuclearInfra: 'Nuclear infrastructure data comes from NRC. Re-run data collection.',
    solar: 'Solar resource data comes from NREL. Re-run data collection or upload an irradiance study.',
    wind: 'Wind resource data comes from NREL. Re-run data collection or upload a wind study.',
    electricity: 'Electricity rate data comes from EIA. Re-run data collection or upload a utility tariff.',
    airQuality: 'Air quality data comes from EPA AirNow. Re-run data collection or upload an attainment letter.',
    connectivity: 'Connectivity data comes from PeeringDB. Re-run data collection or upload a fiber survey / carrier quote.',
    parcel: 'Parcel data comes from Regrid. Re-run data collection or upload a survey / title document.',
    regulatory: 'Regulatory data is AI-derived. Re-run data collection or upload state regulatory filings.',
    siteResearch: 'Site research is AI-derived. Re-run data collection.',
    technology: 'Technology data is derived from the Project Intent. Open the Intent form and specify the reactor design, or run a data refresh.',
    cooling: 'Cooling data is derived from the Project Intent. Open the Intent form and specify the cooling strategy/sink, or run a data refresh.',
    power: 'Power data is derived from the Project Intent. Open the Intent form and specify the power requirements, or run a data refresh.',
    firmCapacity: 'Firm capacity data is derived from the Project Intent. Open the Intent form and specify the capacity target, or run a data refresh.',
    lcoe: 'Financial assumptions are derived from the Project Intent. Open the Intent form and specify the LCOE parameters, or run a data refresh.',
  };
  return help[sectionId] ?? `Add data for "${sectionId}" via document upload, data provider, or contact your admin.`;
}
